import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DIALOG_TYPE } from '../../../constants';
import { ADMIN_PAGES_KEYS, NS } from '../../../i18n/constants/keys';

const PAGE_LABEL_KEYS: Record<string, string> = {
  'Job Opening Details': ADMIN_PAGES_KEYS.jobOpenings.pages.jobOpeningDetails,
  'Applicant Analysis': ADMIN_PAGES_KEYS.jobOpenings.pages.applicantAnalysis,
  'Auto-Assessments': ADMIN_PAGES_KEYS.jobOpenings.pages.autoAssessments,
  'Smart Interviews': ADMIN_PAGES_KEYS.jobOpenings.pages.smartInterviews,
};
import { Dialog } from '../../../ui';
import { ChevronLeft, ChevronRight } from '../../../ui/icons/icons';
import { JobOpeningsContext } from '../../../utils/contexts/JobOpening';
import { CreateJobOpeningDialog } from '../components';
import { useCreateinterview } from './Hooks';
import ProgressTracker from './ui/ProgressTracker';
import { PillStyleTabs } from './ui/PillStyleTabs';

export const JobOpeningsCreate = () => {
  const {
    jobOpeningElements,
    setJobOpeningElements,
    completedJobOpeningDetails,
    setCompletedJobOpeningDetails,
    modalState,
    steps,
    currentPage,
    setCurrentPage,
    renderFormPage,
    setConfirmDialogState,
    setModalState,
    handleModalToggle,
    confirmDialogState,
    stages,
    setStages,
    jobTitleLabel,
    jobDescription,
    show,
    setShow,
    tabCounts,
  } = useCreateinterview();

  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(NS.adminPages);

  return (
    <JobOpeningsContext.Provider
        value={{
          jobOpeningElements,
          setJobOpeningElements,
          completedJobOpeningDetails,
          setCompletedJobOpeningDetails,
          jobOpeningId: id,
        }}
    >
      <div className='!bg-white w-full max-w-full overflow-x-hidden rounded-lg border border-slate-100'>
        <div className='w-full max-w-full overflow-x-hidden !bg-white'>
          { !modalState ? (
            <>
              <nav
                  className='flex w-full flex-col gap-1.5 border-b border-slate-100 px-3 py-1.5 sm:flex-row sm:items-center sm:justify-between'
                  aria-label='Breadcrumb'
              >
                <Link
                    to={'/job-openings-list'}
                    className='group inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-500 hover:text-emerald-600 transition-colors duration-200'
                >
                  <ChevronLeft className='w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5' />
                  { t(ADMIN_PAGES_KEYS.jobOpenings.nav.back) }
                </Link>
                { /* Center - Job Opening Info */ }
                <div className='flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2.5'>
                  <h1 className='max-w-full truncate text-[14px] font-bold text-slate-900 tracking-normal sm:max-w-[42vw] lg:max-w-[520px]'>
                    { (() => {
                      if (
                        typeof jobTitleLabel === 'string' &&
                        jobTitleLabel.trim()
                      ) {
                        return jobTitleLabel;
                      }

                      if (jobDescription?.jobTitle) {
                        if (
                          typeof jobDescription.jobTitle === 'string' &&
                          jobDescription.jobTitle.trim()
                        ) {
                          return jobDescription.jobTitle;
                        }
                        if (
                          typeof jobDescription.jobTitle === 'object' &&
                          jobDescription.jobTitle?.jobTitle
                        ) {
                          return String(jobDescription.jobTitle.jobTitle);
                        }
                      }
                      return t(ADMIN_PAGES_KEYS.jobOpenings.nav.newJobOpening);
                    })() }
                  </h1>
                  <div className='flex w-fit items-center gap-1 px-1.5 py-0.5 bg-slate-50 rounded border border-slate-200/60'>
                    <span className='text-[8px] font-bold text-slate-400 uppercase tracking-wider'>
                      { t(ADMIN_PAGES_KEYS.jobOpenings.nav.ref) }
                    </span>
                    <span className='text-[9px] font-bold text-emerald-700'>
                      { String(
                        jobDescription?.jobReferenceNumber ||
                          `#${id}` ||
                          'Pending',
                      ) }
                    </span>
                  </div>
                </div>

                <Link
                    to={'/jobs'}
                    className='group flex items-center gap-1.5 self-start text-slate-600 font-semibold hover:text-emerald-700 transition-all duration-200 text-[12px] sm:self-auto'
                >
                  <span className='border-b border-transparent group-hover:border-emerald-700 pb-0.5'>
                    { t(ADMIN_PAGES_KEYS.jobOpenings.nav.viewPosting) }
                  </span>
                  <ChevronRight className='w-3 h-3 transition-transform group-hover:translate-x-0.5' />
                </Link>
              </nav>
              <div className='px-3 py-1.5'>
                <ProgressTracker
                    title={jobTitleLabel || t(ADMIN_PAGES_KEYS.jobOpenings.nav.newJobOpening)}
                    globalState='ON GOING'
                    onClose={() => setShow(false)}
                    show={show}
                    setShow={setShow}
                    stages={stages}
                    setStages={setStages}
                    jobOpeningId={id}
                />
              </div>
              <PillStyleTabs
                steps={steps}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                counts={tabCounts}
              />
              { renderFormPage() }
            </>
          ) : (
            <>
              <CreateJobOpeningDialog
                  open={modalState}
                  onClose={() => {
                    setConfirmDialogState(true);
                  }}
                  onSuccessClose={() => setModalState(false)}
              />
              <Dialog
                  title={t(ADMIN_PAGES_KEYS.jobOpenings.cancelDialog.title)}
                  handleApply={handleModalToggle}
                  confirmButtonLable={t(ADMIN_PAGES_KEYS.jobOpenings.cancelDialog.confirm)}
                  isOpen={confirmDialogState}
                  onClose={() => setConfirmDialogState(false)}
                  cancelButtonLable={t(ADMIN_PAGES_KEYS.jobOpenings.cancelDialog.stay)}
                  dialogType={DIALOG_TYPE.WARNING}
                  justify='end'
              >
                <p className='text-primary-textGray text-sm'>
                  { t(ADMIN_PAGES_KEYS.jobOpenings.cancelDialog.message) }
                </p>
              </Dialog>
            </>
          ) }
        </div>
      </div>
    </JobOpeningsContext.Provider>
  );
};
